import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useLocales } from 'src/locales';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'notistack';
import {
  useGetTourClassTimesByTourBundleQuery,
  useBatchUpdateTourClassTimesMutation,
  useBatchCreateTourClassTimesMutation,
} from 'src/redux/slices/tourClassTimeSlice';
import { TourClassTime, TourClassTimeDTO } from 'src/@types/tourClassTime';
import { InfoBox } from '../../../components/map/InfoBox';

interface Props {
  tourBundleId: string;
  onTourClassTimesChange?: (hasTimes: boolean) => void;
}

const getDefaultTourClassTimes = (tourBundleId: number): TourClassTimeDTO[] => [
  {
    durationMin: 0,
    durationMax: 25,
    tourClass: 1,
    tourBundleId,
  },
  {
    durationMin: 25,
    durationMax: 45,
    tourClass: 2,
    tourBundleId,
  },
  {
    durationMin: 45,
    durationMax: 60,
    tourClass: 3,
    tourBundleId,
  },
  {
    durationMin: 60,
    durationMax: 75,
    tourClass: 4,
    tourBundleId,
  },
  {
    durationMin: 75,
    durationMax: 10000,
    tourClass: 5,
    tourBundleId,
  },
];
export default function TourClassTimesManagement({ tourBundleId, onTourClassTimesChange }: Props) {
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [tourClassTimes, setTourClassTimes] = useState<TourClassTimeDTO[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const tourBundleIdNum = useMemo(() => Number(tourBundleId), [tourBundleId]);

  const {
    data: tourClassTimesData,
    isLoading,
    isSuccess,
  } = useGetTourClassTimesByTourBundleQuery(Number(tourBundleId));

  const [batchCreateTourClassTimes, { isLoading: isCreating }] =
    useBatchCreateTourClassTimesMutation();
  const [batchUpdateTourClassTimes, { isLoading: isUpdating }] =
    useBatchUpdateTourClassTimesMutation();

  const isSaving = isCreating || isUpdating;

  const savedTourClassTimes = useMemo(() => {
    if (!tourClassTimesData) return [];
    return Object.values(tourClassTimesData.entities).filter(
      (time): time is TourClassTime => time !== undefined
    );
  }, [tourClassTimesData]);

  const hasExistingData = savedTourClassTimes.length > 0;

  useEffect(() => {
    if (onTourClassTimesChange && !isLoading) {
      onTourClassTimesChange(hasExistingData);
    }
  }, [hasExistingData, isLoading, onTourClassTimesChange]);

  useEffect(() => {
    if (isSuccess) {
      if (savedTourClassTimes.length > 0) {
        setTourClassTimes(savedTourClassTimes);
      } else {
        setTourClassTimes(getDefaultTourClassTimes(tourBundleIdNum));
      }
    }
  }, [isSuccess, savedTourClassTimes, tourBundleIdNum]);

  const handleTourClassTimeChange = (
    index: number,
    field: 'durationMin' | 'durationMax',
    value: string
  ) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) return;

    const newTimes = [...tourClassTimes];
    newTimes[index] = { ...newTimes[index], [field]: numValue };
    setTourClassTimes(newTimes);
    setHasUnsavedChanges(true);
  };

  const handleSaveTourClassTimes = async () => {
    if (!validateTourClassTimes()) {
      enqueueSnackbar(`${translate('tourClassTimes.validationError')}`, {
        variant: 'error',
      });
      return;
    }

    try {
      const hasIds = tourClassTimes.every((time) => time.id !== undefined);

      if (hasIds) {
        await batchUpdateTourClassTimes(tourClassTimes as TourClassTime[]).unwrap();
      } else {
        await batchCreateTourClassTimes(tourClassTimes).unwrap();
      }

      enqueueSnackbar(`${translate('tourClassTimes.saveSuccess')}`, {
        variant: 'success',
      });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving time categories:', error);
      enqueueSnackbar(`${translate('tourClassTimes.saveError')}`, {
        variant: 'error',
      });
    }
  };

  const handleResetChanges = () => {
    if (savedTourClassTimes.length > 0) {
      setTourClassTimes(savedTourClassTimes);
    } else {
      setTourClassTimes(getDefaultTourClassTimes(tourBundleIdNum));
    }
    setHasUnsavedChanges(false);
  };

  const validateTourClassTimes = () => {
    for (let i = 0; i < tourClassTimes.length; i++) {
      // Check if durationMax is less than durationMin
      if (tourClassTimes[i].durationMax < tourClassTimes[i].durationMin) {
        return false;
      }
      // Check if consecutive ranges are continuous
      if (i < tourClassTimes.length - 1) {
        if (tourClassTimes[i].durationMax !== tourClassTimes[i + 1].durationMin) {
          return false;
        }
      }
    }
    return true;
  };

  const getButtonText = () => {
    if (!hasExistingData) {
      return `${translate('tourClassTimes.createTimes')}`;
    }
    return `${translate('tourClassTimes.saveChanges')}`;
  };

  const isButtonDisabled = hasExistingData && !hasUnsavedChanges;

  if (isLoading) {
    return (
      <Card sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">{`${translate('tourClassTimes.title')}`}</Typography>
      </Stack>
      <InfoBox title={`${translate('tourClassTimes.infoDescription')}`} />
      {!validateTourClassTimes() && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: 'error.lighter',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Iconify icon="eva:alert-triangle-fill" color="error.main" />
          <Typography variant="body2" color="error.main">
            {`${translate('tourClassTimes.validationError')}`}
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">{`${translate('tourClassTimes.tourClass')}`}</TableCell>
              <TableCell align="center">{`${translate('tourClassTimes.durationMin')}`}</TableCell>
              <TableCell align="center">{`${translate('tourClassTimes.durationMax')}`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tourClassTimes.map((time, index) => (
              <TableRow key={time.tourClass}>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight="bold">
                    {`${translate('tourClassTimes.class', { number: time.tourClass })}`}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    value={time.durationMin}
                    onChange={(e) =>
                      handleTourClassTimeChange(index, 'durationMin', e.target.value)
                    }
                    inputProps={{ min: 0 }}
                    sx={{ width: 120 }}
                    error={
                      (index > 0 && time.durationMin !== tourClassTimes[index - 1].durationMax) ||
                      time.durationMin > time.durationMax
                    }
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    size="small"
                    value={time.durationMax}
                    onChange={(e) =>
                      handleTourClassTimeChange(index, 'durationMax', e.target.value)
                    }
                    inputProps={{ min: 0 }}
                    sx={{ width: 120 }}
                    error={
                      (index < tourClassTimes.length - 1 &&
                        time.durationMax !== tourClassTimes[index + 1].durationMin) ||
                      time.durationMax < time.durationMin
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        {hasUnsavedChanges && hasExistingData && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Iconify icon="eva:refresh-fill" />}
            onClick={handleResetChanges}
            sx={{ textTransform: 'none' }}
          >
            {`${translate('tourClassTimes.reset')}`}
          </Button>
        )}
        <Button
          variant="contained"
          color="success"
          startIcon={<Iconify icon="eva:save-fill" />}
          onClick={handleSaveTourClassTimes}
          disabled={isSaving || isButtonDisabled}
          sx={{ textTransform: 'none' }}
        >
          {isSaving ? `${translate('saving')}` : getButtonText()}
        </Button>
      </Stack>
    </Card>
  );
}
